import { useSearchParams } from 'react-router';

/**
 * A list page's filters and paging, held in the URL (`?search=…&status=…&page=2&pageSize=50`), so
 * a filtered list survives a refresh, can be bookmarked or shared, and a dashboard figure can link
 * straight to the list behind it (FRD-RPT-007). The server answers every filter and page
 * (FRD-CORE-038); nothing is filtered in the browser.
 *
 * The URL's `page` counts from 1, like the API's; the `page` returned here counts from 0, as
 * MUI's `TablePagination` does.
 */
export function useUrlList(defaults: { pageSize?: number } = {}) {
  const [params, setParams] = useSearchParams();
  const defaultPageSize = defaults.pageSize ?? 25;
  const page = Math.max(0, Number.parseInt(params.get('page') ?? '1', 10) - 1 || 0);
  const pageSize = Number.parseInt(params.get('pageSize') ?? '', 10) || defaultPageSize;

  /** A filter's value, or '' when absent. */
  const get = (key: string): string => params.get(key) ?? '';

  /** Sets (or, when empty, removes) one filter; any filter change starts again from page one. */
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(key);
    else next.set(key, value);
    next.delete('page');
    setParams(next, { replace: true });
  };

  /** Sets several filters at once (an empty value removes one), from page one. */
  const setMany = (values: Readonly<Record<string, string>>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(values)) {
      if (value === '') next.delete(key);
      else next.set(key, value);
    }
    next.delete('page');
    setParams(next, { replace: true });
  };

  const setPage = (zeroBased: number) => {
    const next = new URLSearchParams(params);
    if (zeroBased <= 0) next.delete('page');
    else next.set('page', String(zeroBased + 1));
    setParams(next, { replace: true });
  };

  const setPageSize = (size: number) => {
    const next = new URLSearchParams(params);
    if (size === defaultPageSize) next.delete('pageSize');
    else next.set('pageSize', String(size));
    next.delete('page');
    setParams(next, { replace: true });
  };

  /**
   * The API query for these filters: `page`, `pageSize`, `sort` and `filter[<name>]` for each
   * named URL key that holds a value (`apiFilters` maps a URL key to the API's filter name).
   */
  const apiParams = (apiFilters: Readonly<Record<string, string>>, sort: string) => {
    const query = new URLSearchParams({
      page: String(page + 1),
      pageSize: String(pageSize),
      sort,
    });
    for (const [urlKey, filterName] of Object.entries(apiFilters)) {
      const value = get(urlKey).trim();
      if (value !== '') query.set(`filter[${filterName}]`, value);
    }
    return query;
  };

  return { page, pageSize, get, set, setMany, setPage, setPageSize, apiParams };
}
